/**
 * 小程序 `CloudAuthStorage` 适配（对应核心 SDK `types.ts` 的
 * "非浏览器运行时（Node、小程序）里没有 localStorage" 那条注释）。
 */

import type { CloudAuthStorage } from '../../modules/auth/types.js';
import type { MiniProgramWxLike } from './wx-types.js';

/**
 * 用 `wx.getStorageSync`/`setStorageSync`/`removeStorageSync` 实现 `CloudAuthStorage`。
 *
 * ⚠️ 占位说明：小程序同步存储 API 在容量超限或用户拒绝授权等场景可能抛异常，
 * 这里按"存储不可用时静默降级、不阻塞登录态读写"的策略吞掉异常并打印
 * warning——具体降级策略（是否应该报错给上层）留给接入方按真机表现调整，
 * 现在只是先跑通不炸。
 */
export function createMiniProgramStorage(
    wxInstance: MiniProgramWxLike = (globalThis as { wx?: MiniProgramWxLike }).wx as MiniProgramWxLike,
): CloudAuthStorage {
    if (!wxInstance) {
        throw new Error(
            'createMiniProgramStorage: 未找到全局 wx 对象，请在小程序环境调用，或显式传入 wxInstance。',
        );
    }

    return {
        getItem(key: string): string | null {
            try {
                const value = wxInstance.getStorageSync(key);
                if (value === undefined || value === null || value === '') {
                    return null;
                }
                return typeof value === 'string' ? value : JSON.stringify(value);
            } catch (err) {
                console.warn('[MiniProgramStorage] getItem failed', key, err);
                return null;
            }
        },
        setItem(key: string, value: string): void {
            try {
                wxInstance.setStorageSync(key, value);
            } catch (err) {
                console.warn('[MiniProgramStorage] setItem failed', key, err);
            }
        },
        removeItem(key: string): void {
            try {
                wxInstance.removeStorageSync(key);
            } catch (err) {
                console.warn('[MiniProgramStorage] removeItem failed', key, err);
            }
        },
    };
}
